import React from "react";
import { approveEventCandidate, rejectEventCandidate, getEventCandidatePreview } from "../lib/api";
import { eventInstant, savedEligibility } from "../lib/savedEventReview";

const REVIEW = "/admin#event-candidates";
function when(item) {
  const date = eventInstant(item.start);
  return date
    ? `${new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Madrid", dateStyle: "medium", timeStyle: "short" }).format(date)} · Europe/Madrid`
    : "Date/time unresolved · Europe/Madrid";
}
function blockers(item) {
  return [...new Set([...(item.review_reasons || []),
    ...(!item.venue_id || !item.venue_name ? ["canonical_venue_missing"] : []),
    ...(!item.start || Number.isNaN(new Date(item.start).getTime()) ? ["date_or_time_unknown"] : []),
    ...(item.start && new Date(item.start).getTime() < Date.now() ? ["expired"] : []),
    ...(item.verification !== "supported_for_review" ? ["field_evidence_missing_or_requires_validation"] : [])])];
}
const eventLink = (id, path) => `${path === "/tonight-torremolinos" ? path : "/tonight-benalmadena"}#event-${encodeURIComponent(id)}`;

export function EventApprovalCard({ item, onChange, savedCandidate, children, onPreview }) {
  const [loaded, setLoaded] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const lock = React.useRef(false);
  const previewCallback = React.useRef(onPreview);
  previewCallback.current = onPreview;
  const saved = loaded || savedCandidate;
  React.useEffect(() => { setLoaded(null); }, [savedCandidate]);
  React.useEffect(() => {
    if (savedCandidate) return;
    let active = true;
    getEventCandidatePreview(item.id).then(value => { if (value?.id !== item.id || !value.publication_version) throw new Error("Invalid saved preview"); if (active) { setLoaded(value); previewCallback.current?.(value); } })
      .catch(() => { if (active) setError("Saved preview unavailable. Open candidate review or refresh before publishing."); });
    return () => { active = false; };
  }, [item.id, savedCandidate]);
  const view = saved ? {...item, title:saved.title, venue_name:saved.venue_name,
    start:saved.start_datetime, price:saved.price_text, source_url:saved.source_post_url,
    status:saved.status, approved_event_id:saved.approved_event_id} : item;
  const eligibility = saved ? savedEligibility(saved) : null;
  const incomplete = saved && (!eligibility.dateKnown || !saved.venue_id);
  const reasons = eligibility?.reasons || [];
  const published = view.status === "approved" && view.approved_event_id;
  const pending = view.status === "pending" || !view.status;
  const displayWhen = saved && !saved.start_datetime && saved.resolved_event_date
    ? `${saved.resolved_event_date} · Time not supplied · Europe/Madrid` : when(view);
  async function decide(action) {
    if (lock.current) return;
    if (action === "approve" && (!saved?.publication_version || savedEligibility(saved).hardBlocked)) return;
    lock.current = true; setBusy(true); setError("");
    try {
      if (action === "approve") {
        const result = await approveEventCandidate(item.id, {publication_version:saved.publication_version});
        if (!result?.ok || result.event_status !== "approved" || !result.event_id || result.duplicate_detected)
          throw new Error("Publication was not confirmed. Refresh the saved queue before retrying.");
        onChange({...view, status:"approved", approved_event_id:result.event_id, public_event_path:result.public_path});
        setLoaded({...saved, status:"approved", approved_event_id:result.event_id, public_event_path:result.public_path});
      } else {
        const result = await rejectEventCandidate(item.id, "Rejected by Admin after reviewing the original evidence and prepared listing");
        if (!result?.ok || result.status !== "rejected") throw new Error("Rejection was not confirmed. Please refresh and try again.");
        onChange({...view, status:"rejected"});
        if (saved) setLoaded({...saved, status:"rejected"});
      }
      window.dispatchEvent(new Event("saved-event-queue-changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.candidate) { setLoaded(detail.candidate); setEditing(false); }
      setError(typeof detail === "string" ? detail : detail?.message || err.message || "Unable to save. Please try again.");
    } finally { lock.current = false; setBusy(false); }
  }
  return <article className="min-w-0 break-words rounded-xl border border-white/20 p-3 space-y-3" aria-label={view.title}>
    {!published && <p className="font-bold text-cyan-100">{!pending ? view.status : !saved ? "Loading saved preview" : incomplete ? "Incomplete" : eligibility.hardBlocked ? "Publication blocked" : "Ready to publish"}</p>}
    <div className="grid gap-4 lg:grid-cols-2">
      <section aria-label="Original source evidence">
        <h4 className="font-bold">Original advert / source evidence</h4>
        <SourceLink url={view.source_url}>Original source</SourceLink>
        {saved?.image_url && /^https?:\/\//i.test(saved.image_url) && <img src={saved.image_url} alt="Original event advert" className="max-h-72 max-w-full object-contain" />}
        <p className="whitespace-pre-wrap">{saved?.source_text || "Source evidence not loaded."}</p>
      </section>
      <section aria-label="Exact prepared public listing">
        <h4 className="font-bold text-base">{view.title}</h4>
        <p>{view.venue_name || "Actual venue unresolved"}</p><p>{displayWhen}</p>
        {saved?.end_datetime && <p>Ends: {when({start:saved.end_datetime})}</p>}
        <p>Performer: {saved?.performer || "Not supplied"} · Price: {view.price || "Unknown"}</p>
        <p>Category: {saved?.event_type?.replace(/_/g, " ") || "Unknown"}</p>
        <p className="whitespace-pre-wrap">{saved?.public_description ?? saved?.description}</p>
      </section>
    </div>
    {!!reasons.length && <p className="text-amber-100">{incomplete ? reasons[0] : reasons.join(" ")}</p>}
    {saved?.manual_reason && <p>{saved.manual_reason}</p>}
    {saved?.extraction_notes && <p>{saved.extraction_notes}</p>}
    {!incomplete && !!eligibility?.reviewReasons.length && <p>{eligibility.reviewReasons.join(" ")} Your PUBLISH LIVE click records your review and approval.</p>}
    {published ? <p role="status"><strong>PUBLISHED</strong> · <a className="underline text-cyan-200" href={eventLink(view.approved_event_id, saved?.public_event_path || view.public_event_path)}>View published event</a></p> : pending && <>
      {children && (!loaded || loaded.publication_version === savedCandidate?.publication_version) && <><button type="button" className="min-h-[44px] px-4 underline" onClick={() => setEditing(value => !value)} aria-expanded={editing}>Edit</button>{editing && children}</>}
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" className="min-h-[52px] w-full rounded-lg bg-cyan-300 px-4 py-3 font-bold text-black disabled:opacity-40" disabled={busy || !saved?.publication_version || eligibility?.hardBlocked} onClick={() => decide("approve")}>{busy ? "Saving…" : "PUBLISH LIVE"}</button>
        <button type="button" className="min-h-[48px] rounded-lg border border-white/40 px-4 py-3 font-bold" disabled={busy} onClick={() => decide("reject")}>REJECT</button>
      </div>
      <p className="text-xs">PUBLISH LIVE approves this exact listing and makes it public. It does not publish a social post.</p>
    </>}
    {error && <p role="alert">{error}</p>}
  </article>;
}

function SourceLink({ url, children }) {
  try {
    const parsed = new URL(url);
    if (!["https:", "http:"].includes(parsed.protocol) || parsed.username || parsed.password) return <span>{children}</span>;
  } catch (_) { return <span>{children}</span>; }
  return <a className="underline text-cyan-200" href={url} target="_blank" rel="noreferrer">{children}</a>;
}

export default function VibeBeeResearchResult({ research }) {
  const [changes, setChanges] = React.useState({});
  const [previews, setPreviews] = React.useState({});
  React.useEffect(() => { setChanges({}); setPreviews({}); }, [research]);
  if (!research) return null;
  const failures = research.discovery?.source_failures || [];
  const items = (research.candidates || []).map(item => changes[item.id] || item);
  const group = item => {
    if (item.status === "approved" && item.approved_event_id) return "published";
    if (item.status === "rejected" || item.review_reasons?.includes("rejected")) return "rejected";
    const saved = previews[item.id];
    if (saved) {
      const eligible = savedEligibility(saved);
      if (!saved.venue_id || !eligible.dateKnown) return "incomplete";
      if (eligible.hardBlocked || (item.status && item.status !== "pending")) return "unresolved";
      return "pending";
    }
    if (item.review_reasons?.some(r => ["expired", "outside_requested_window"].includes(r))) return "expired";
    return blockers(item).length ? "unresolved" : "pending";
  };
  const render = item => <EventApprovalCard key={item.id} item={item}
    savedCandidate={previews[item.id] ? {...previews[item.id],status:item.status || previews[item.id].status,approved_event_id:item.approved_event_id || previews[item.id].approved_event_id} : undefined}
    onPreview={value => setPreviews(old => ({...old,[item.id]:value}))}
    onChange={next => setChanges(old => ({ ...old, [item.id]: next }))} />;
  return <section aria-label="Live research results" className="mt-3 space-y-3 text-sm">
    <h3 className="font-bold text-base">EVENTS AWAITING YOUR APPROVAL</h3>
    {items.filter(item => group(item) === "pending").map(render)}
    {!items.some(item => group(item) === "pending") && <p>No events are ready for immediate approval. Check unresolved items below.</p>}
    {items.filter(item => group(item) === "published").map(render)}
    {[['incomplete', 'Incomplete'], ['unresolved', 'Unresolved items — extra review required'], ['rejected', 'Rejected items'], ['expired', 'Expired or outside this research window']].map(([key, label]) => <details key={key}><summary>{label} ({items.filter(item => group(item) === key).length})</summary><div className="space-y-2 mt-2">{items.filter(item => group(item) === key).map(render)}</div></details>)}
    <a className="underline text-cyan-200" href={REVIEW}>Open candidate review</a>
    <details><summary>Sources and technical diagnostics</summary>
    <p>Research window: {research.window_start} – {research.window_end}. Publication requires your separate approval.</p>
    <details><summary>Sources checked and deferred</summary>
      <ul>{research.sources?.map(source => <li key={source.id}><SourceLink url={source.url}>{source.name}</SourceLink> · {source.area}</li>)}</ul>
      {research.areas_without_sources?.map(area => <p key={area}>No eligible sources: {area}</p>)}
      <p>{research.deferred_sources?.length || 0} sources deferred by freshness or request limits.</p>
      {failures.map((failure, index) => <p key={index}>{failure.source_name || "Source"}: {failure.failure_category || failure.error_class || "Unavailable"}</p>)}
    </details>
    <details><summary>Historical source evidence</summary>
      {!research.candidates?.length && <p>No candidates available for this scope.</p>}
      {research.candidates?.map(item => <article key={item.id} className="my-2 rounded border border-white/10 p-2">
        <strong>{item.title}</strong><p>{item.start || "Date/time unresolved"} · {item.price || "Price unknown"}</p>
        <p>{item.geography?.locality || item.geography?.municipality || "Location needs review"}</p>
        <SourceLink url={item.source_url}>Source evidence</SourceLink>
        <p>Last successful source check: {item.checked_at || "Unknown"}</p>
        <p>{item.review_reasons?.length ? item.review_reasons.join(", ") : "Supported evidence; awaiting your approval"}</p>
      </article>)}
    </details>
    <details><summary>Items excluded or needing investigation ({research.investigations?.length || 0})</summary>
      {research.investigations?.map((item, index) => <article key={index} className="my-2">
        <SourceLink url={item.source_url}>{item.source_name || "Source"}</SourceLink>
        <p>{item.reason}</p><p>{item.evidence}</p><p>Checked {item.checked_at || "Unknown"}</p>
      </article>)}
    </details>
    <details><summary>Dated local knowledge from these sources</summary>
      <p>{research.knowledge_limitations}</p>
      {research.knowledge?.map((item, index) => <blockquote key={index} className="my-2 border-l border-cyan-200/30 pl-2">
        <SourceLink url={item.source_url}>{item.source_name}</SourceLink><p>{item.excerpt}</p>
        <p>{item.cached ? "Saved excerpt. " : ""}Retrieved {item.retrieved_at}. {item.uncertainty}</p>
      </blockquote>)}
    </details>
    </details>
  </section>;
}
